import React, { useState, useEffect, useCallback } from 'react';
import { fmtSignalTime, fmtUsd, fmtDollar, DISPLAY_TZ } from '../utils/fmtPrice';
import { formatSideLabel, sideLabelCssClass } from '../utils/tradeActionUi';
import './SignalQueueSection.css';
import './OBotTrading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const UI_BUILD = 'sq-2026-06-11c';

function fmtRangeStacked(s) {
  const stop = s.stopPrice ?? s.stop_price;
  const target = s.targetPrice ?? s.target_price;
  const label = s.rangeLabel;
  const hasStop = stop != null && stop !== '' && Number.isFinite(Number(stop));
  const hasTarget = target != null && target !== '' && Number.isFinite(Number(target));
  const hasLabel = label && label !== '—';

  if (!hasStop && !hasTarget && !hasLabel) return '—';

  return (
    <>
      {hasStop ? (
        <span className="signal-range-stop">Stop {fmtDollar(Number(stop))}</span>
      ) : null}
      {hasTarget ? (
        <span className="signal-range-target">Target {fmtDollar(Number(target))}</span>
      ) : null}
      {hasLabel ? <span className="signal-range-label">{label}</span> : null}
    </>
  );
}

function fmtSignalTimeStacked(isoOrSql) {
  if (!isoOrSql) return { date: '—', time: '' };
  const normalized = String(isoOrSql).includes('T')
    ? isoOrSql
    : `${String(isoOrSql).replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return { date: isoOrSql, time: '' };
  return {
    date: d.toLocaleString('en-US', {
      timeZone: DISPLAY_TZ,
      month: 'short',
      day: 'numeric',
    }),
    time: d.toLocaleString('en-US', {
      timeZone: DISPLAY_TZ,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }),
  };
}

function fmtSymbolStacked(symbol, midPrice) {
  if (midPrice == null || !Number.isFinite(Number(midPrice))) {
    return <span className="signal-symbol-name">{symbol}</span>;
  }
  return (
    <>
      <span className="signal-symbol-name">{symbol}</span>
      <span className="signal-symbol-mid">{fmtDollar(midPrice)}</span>
    </>
  );
}

function fmtMarginWithPct(s) {
  const usdc = s.traderUsdc ?? s.size_usd;
  const deployable = s.traderDeployableUsd;
  const showAvail =
    deployable != null &&
    Number.isFinite(Number(deployable)) &&
    usdc != null &&
    Number.isFinite(Number(usdc)) &&
    Number(deployable) + 0.5 < Number(usdc);

  const pctParts = [];
  if (s.mapWeightPct != null && Number.isFinite(Number(s.mapWeightPct))) {
    pctParts.push(`${Number(s.mapWeightPct).toFixed(2)}% MAP`);
  }
  if (s.traderMarginPct != null && Number.isFinite(Number(s.traderMarginPct))) {
    pctParts.push(`${Number(s.traderMarginPct).toFixed(2)}% wallet`);
  }
  if (showAvail) {
    pctParts.push(`${fmtUsd(deployable)} avail`);
  }

  if (!pctParts.length) {
    return (
      <>
        <span className="signal-margin-usd">{fmtUsd(usdc)}</span>
      </>
    );
  }
  return (
    <>
      <span className="signal-margin-usd">{fmtUsd(usdc)}</span>
      <span className="signal-margin-pct">{pctParts.join(' · ')}</span>
    </>
  );
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
});

function sideClass(side, action) {
  return sideLabelCssClass(side, action);
}

function sideDisplay(side, action) {
  return formatSideLabel(side, action);
}

async function fetchSignals(limit, { sizing, feed } = {}) {
  const url = new URL(`${API_BASE}/multi-asset/signals`);
  url.searchParams.set('limit', String(limit));
  if (sizing) url.searchParams.set('sizing', sizing);
  if (feed) url.searchParams.set('feed', feed);
  url.searchParams.set('_', String(Date.now()));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: authHeaders(),
    cache: 'no-store',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

function ManualSignalTable({ signals, scrollable, visibleRows, onTradeSignal }) {
  const scrollStyle =
    scrollable && visibleRows ? { maxHeight: `${38 + visibleRows * 38}px` } : undefined;

  const table = (
    <table className="signal-queue-table signal-queue-table-manual">
      <colgroup>
        <col className="col-id" />
        <col className="col-time" />
        <col className="col-symbol" />
        <col className="col-side" />
        <col className="col-margin" />
        <col className="col-range" />
        <col className="col-trade" />
      </colgroup>
      <thead>
        <tr>
          <th>ID</th>
          <th>Time</th>
          <th>Symbol</th>
          <th>Side</th>
          <th>Margin</th>
          <th>Range</th>
          <th className="signal-trade-cell">Trade</th>
        </tr>
      </thead>
      <tbody>
        {signals.map((s) => {
          const when = fmtSignalTimeStacked(s.created_at);
          const inPosition = !!s.alreadyInPosition;
          return (
          <tr key={s.id}>
            <td className="signal-id-cell">{s.id}</td>
            <td className="signal-time-cell signal-time-stacked">
              <span className="signal-time-date">{when.date}</span>
              {when.time ? <span className="signal-time-clock">{when.time}</span> : null}
            </td>
            <td className="signal-symbol-cell">{fmtSymbolStacked(s.symbol, s.midPrice)}</td>
            <td className={sideClass(s.side, s.action)}>{sideDisplay(s.side, s.action)}</td>
            <td className="signal-margin-cell">{fmtMarginWithPct(s)}</td>
            <td className="range-cell signal-range-stacked">{fmtRangeStacked(s)}</td>
            <td className="signal-trade-cell">
              <button
                type="button"
                className="primary-btn signal-trade-btn"
                onClick={() => onTradeSignal?.(s)}
                disabled={!onTradeSignal || inPosition}
                title={
                  inPosition
                    ? `Already in ${sideDisplay(s.side, s.action)} ${s.symbol}`
                    : 'Fill trade panel from this signal'
                }
              >
                {inPosition ? 'In position' : 'Trade'}
              </button>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="signal-queue-desktop">
        <div
          className={`signal-queue-table-wrap${scrollable ? ' signal-queue-table-scroll' : ''}`}
          style={scrollStyle}
        >
          {table}
        </div>
      </div>
      <div
        className={`signal-queue-mobile${scrollable ? ' signal-queue-mobile-scroll' : ''}`}
        style={scrollStyle}
      >
        {signals.map((s) => {
          const when = fmtSignalTimeStacked(s.created_at);
          const inPosition = !!s.alreadyInPosition;
          const showAvail =
            s.traderDeployableUsd != null &&
            s.traderUsdc != null &&
            Number(s.traderDeployableUsd) + 0.5 < Number(s.traderUsdc);
          return (
          <article key={s.id} className="signal-queue-mobile-card signal-queue-mobile-manual">
            <div className="signal-queue-mobile-top">
              <span className="signal-queue-mobile-id">#{s.id}</span>
              <span className="signal-queue-mobile-symbol">{s.symbol}</span>
              {s.midPrice != null && Number.isFinite(Number(s.midPrice)) ? (
                <span className="signal-queue-mobile-mid">{fmtDollar(s.midPrice)}</span>
              ) : null}
              <span className={`signal-queue-mobile-side ${sideClass(s.side, s.action)}`}>
                {sideDisplay(s.side, s.action)}
              </span>
            </div>
            <p className="signal-queue-mobile-time signal-time-stacked">
              <span className="signal-time-date">{when.date}</span>
              {when.time ? <span className="signal-time-clock">{when.time}</span> : null}
            </p>
            <div className="signal-queue-mobile-grid signal-queue-mobile-grid-manual">
              <div className="signal-margin-cell">
                <span>Suggested margin </span>
                <strong>{fmtUsd(s.traderUsdc ?? s.size_usd)}</strong>
                {(s.mapWeightPct != null || s.traderMarginPct != null || showAvail) && (
                  <span className="signal-margin-pct">
                    {s.mapWeightPct != null ? `${Number(s.mapWeightPct).toFixed(2)}% MAP` : ''}
                    {s.mapWeightPct != null && s.traderMarginPct != null ? ' · ' : ''}
                    {s.traderMarginPct != null ? `${Number(s.traderMarginPct).toFixed(2)}% wallet` : ''}
                    {showAvail ? ` · ${fmtUsd(s.traderDeployableUsd)} avail` : ''}
                  </span>
                )}
              </div>
              <div className="signal-queue-mobile-range signal-range-stacked">
                <span>Range </span>
                {fmtRangeStacked(s)}
              </div>
            </div>
            <button
              type="button"
              className="primary-btn signal-trade-btn signal-trade-btn-mobile"
              onClick={() => onTradeSignal?.(s)}
              disabled={!onTradeSignal || inPosition}
              title={inPosition ? `Already in ${sideDisplay(s.side, s.action)} ${s.symbol}` : undefined}
            >
              {inPosition ? 'In position' : 'Trade'}
            </button>
          </article>
          );
        })}
      </div>
    </>
  );
}

function FullSignalTable({ signals }) {
  return (
    <>
      <div className="signal-queue-desktop">
        <div className="signal-queue-table-wrap">
          <table className="signal-queue-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Time (Central)</th>
                <th>Symbol</th>
                <th>Action</th>
                <th>Type</th>
                <th>USD</th>
                <th>Lev</th>
                <th>Status</th>
                <th>Source</th>
                <th>Reason / error</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td className="signal-time-cell">{fmtSignalTime(s.created_at)}</td>
                  <td>{s.symbol}</td>
                  <td className={sideClass(s.side, s.action)}>{sideDisplay(s.side, s.action)}</td>
                  <td>{s.action}</td>
                  <td>{fmtUsd(s.traderUsdc ?? s.size_usd)}</td>
                  <td>{s.leverage != null ? `${s.leverage}x` : '—'}</td>
                  <td className={`st-${s.status}`}>{s.status}</td>
                  <td>{s.source}</td>
                  <td className="reason-cell">{s.error_message || s.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="signal-queue-mobile">
        {signals.map((s) => (
          <article key={s.id} className="signal-queue-mobile-card">
            <div className="signal-queue-mobile-top">
              <span className="signal-queue-mobile-id">#{s.id}</span>
              <span className="signal-queue-mobile-symbol">{s.symbol}</span>
              <span className={`signal-queue-mobile-side ${sideClass(s.side, s.action)}`}>
                {sideDisplay(s.side, s.action)}
              </span>
              <span className={`signal-queue-mobile-status st-${s.status}`}>{s.status}</span>
            </div>
            <p className="signal-queue-mobile-time">{fmtSignalTime(s.created_at)}</p>
            <div className="signal-queue-mobile-grid">
              <div>
                <span>Action </span>
                <strong>{s.action}</strong>
              </div>
              <div>
                <span>USD </span>
                <strong>{fmtUsd(s.traderUsdc ?? s.size_usd)}</strong>
              </div>
              <div>
                <span>Lev </span>
                <strong>{s.leverage != null ? `${s.leverage}x` : '—'}</strong>
              </div>
              <div>
                <span>Source </span>
                <strong>{s.source}</strong>
              </div>
            </div>
            {(s.error_message || s.reason) && (
              <p className="signal-queue-mobile-reason">{s.error_message || s.reason}</p>
            )}
          </article>
        ))}
      </div>
    </>
  );
}

/**
 * Shared signal queue (Omni MAP algorithm, chat ingest, manual orders).
 * variant="manual" — simplified columns for Manual Trades reference.
 */
export default function SignalQueueSection({
  refreshKey = 0,
  pollMs = 30000,
  limit = 50,
  visibleRows = null,
  hint = 'Omni MAP algorithm, chat ingest, or manual orders.',
  title = 'Signal queue',
  variant = 'full',
  onLatestSignal = null,
  latestSignalFilter = null,
  feed,
  enabled = true,
  onTradeSignal = null,
}) {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [apiUserId, setApiUserId] = useState(null);
  const [gateInfo, setGateInfo] = useState(null);

  const isManual = variant === 'manual';
  const scrollable = isManual && visibleRows != null && visibleRows > 0;

  const load = useCallback(async () => {
    if (!enabled) {
      setSignals([]);
      setLoading(false);
      return;
    }
    setLoadError(null);
    setLoading(true);
    try {
      const data = await fetchSignals(limit, {
        sizing: isManual ? 'wallet' : undefined,
        feed: feed || (isManual ? 'manual' : 'obot'),
      });
      const next = Array.isArray(data.signals) ? data.signals : [];
      setSignals(next);
      setApiUserId(data.userId ?? null);
      setGateInfo(data.gated ? data.gateReason || 'Real-time signals are locked.' : null);
      setLastFetchedAt(new Date());
    } catch (e) {
      setLoadError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [limit, isManual, feed, enabled]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  useEffect(() => {
    if (!onLatestSignal || !signals.length) return;
    const pick = latestSignalFilter ? latestSignalFilter(signals) : signals[0];
    if (pick) onLatestSignal(pick);
  }, [signals, onLatestSignal, latestSignalFilter]);

  const topId = signals[0]?.id;

  return (
    <section className="obot-card signal-queue-card">
      <div className="signal-queue-header">
        <div className="signal-queue-header-text">
          <h2>
            {title}
            {!loading && signals.length > 0 ? ` (${signals.length})` : ''}
          </h2>
          {lastFetchedAt && (
            <p className="signal-queue-meta">
              Updated {fmtSignalTime(lastFetchedAt.toISOString())} (Central)
              {scrollable ? ` · showing up to ${visibleRows} rows — scroll for ${limit}` : ''}
              {pollMs ? ` · auto-refresh ${Math.round(pollMs / 1000)}s` : ''}
              {apiUserId != null ? ` · user #${apiUserId}` : ''}
              {topId != null ? ` · latest #${topId}` : ''}
              {` · ${UI_BUILD}`}
            </p>
          )}
        </div>
        <button type="button" className="secondary-btn signal-queue-refresh" onClick={load}>
          Refresh
        </button>
      </div>
      <p className="hint signal-queue-hint">{hint}</p>
      {gateInfo && (
        <div className="obot-error signal-queue-gate">
          🔒 {gateInfo}
          {gateInfo.includes('builder') || gateInfo.includes('commission')
            ? ' Open the wallet gear or complete signal access purchase above.'
            : ' Purchase weekly or monthly signal access above.'}
        </div>
      )}
      {loadError && <div className="obot-error">{loadError}</div>}
      {loading && signals.length === 0 ? (
        <p className="signal-queue-empty">Loading signals…</p>
      ) : signals.length === 0 ? (
        <p className="signal-queue-empty">No signals yet.</p>
      ) : isManual ? (
        <ManualSignalTable
          signals={signals}
          scrollable={scrollable}
          visibleRows={visibleRows}
          onTradeSignal={onTradeSignal}
        />
      ) : (
        <FullSignalTable signals={signals} />
      )}
    </section>
  );
}
